import React, { FC } from 'react';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { FaEdit } from 'react-icons/fa';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import { CellProps, Column } from 'react-table';
import Button from 'components/Button';
import Badge from 'components/Badge';
import { useHistory } from 'react-router';
import TooltipWrapper from 'components/TooltipWrapper';
import DeleteEntity from 'components/crud/DeleteEntity';
import { buildFilters, filters } from 'components/table/utils/filtersConfig';
import { useTranslation } from 'react-i18next';
import { IField } from '../types';

export interface IProps {
  entityName: string;
  entityEndpoint: string;
}

const List: FC<IProps> = ({ entityName, entityEndpoint }) => {
  const history = useHistory();
  const { client } = useActiveClientContext();
  const createField = () => history.push(buildClientUrl('/fields/create', client));

  const { t } = useTranslation();

  const columns: Array<Column<IField>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Име',
      accessor: 'name',
      width: '100%',
    },
    {
      Header: 'Идентификатор',
      accessor: 'identifier',
    },
    {
      Header: 'Тип',
      accessor: 'type',
      width: '100%',
      minWidth: 180,
      disableSortBy: true,
      Cell: ({ row }: CellProps<IField>) => {
        const { type } = row.original;

        if (type !== 'datepicker') {
          const typeOption = filters.fieldType.options.find(opt => opt.value === type);
          return <div>{typeOption?.label || type}</div>;
        }
        return <div>{type}</div>;
      },
    },
    {
      Header: 'Статус',
      accessor: 'status',
      width: '100%',
      minWidth: 100,
      disableSortBy: true,
      Cell: ({ cell }: CellProps<IField>) => {
        if (cell.row.original.is_active)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => <div className="text-right">Действия</div>,
      accessor: 'actions',
      disableSortBy: true,
      maxWidth: 120,
      width: 120,
      minWidth: 120,
      Cell: ({ cell }: CellProps<IField>) => {
        const { id, name, can_edit: canEdit } = cell.row.original;
        return (
          <div className="text-right">
            <div className="d-inline-block mr-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay={canEdit ? 'Редактирай' : 'Глобалните полета не могат да бъдат редактирани'}
              >
                <Button
                  btnType="primary"
                  size="sm"
                  outlined
                  onClick={() => history.push(buildClientUrl(`/fields/edit/${id}`, client))}
                  disabled={!canEdit}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
            <DeleteEntity
              id={id}
              entity={entityName}
              successMsg="Полето беше изтрито."
              confirmHeading="Изтриване на поле"
              confirmMsg={
                <span>
                  Сигурни ли сте, че желаете да изтриете поле&nbsp;
                  <span className="font-weight-bold">{name}</span>?
                </span>
              }
              queryKey={buildClientEntity(entityName, client)}
              isDisabled={!canEdit}
              disabledReason="Глобалните полета не могат да бъдат изтрити"
            />
          </div>
        );
      },
    },
  ];

  const tableFilters = [
    buildFilters({ ...filters.fieldType, t, translatable: true }),
    buildFilters({ ...filters.status, t, translatable: true }),
  ];

  return (
    <MainLayout title="Управление на полета">
      <FetchTable<IField>
        entityName={buildClientEntity(entityName, client)}
        entityEndpoint={entityEndpoint}
        columns={columns}
        onAdd={createField}
        addBtnLabel="Добавяне на поле"
        pagination
        filters={tableFilters}
      />
    </MainLayout>
  );
};

export default List;
